/**
 * Signed appliance software updates, gated by maintenance.
 *
 * One module read by the staff build tool (scripts/build-update.ts), the cloud
 * (release catalogue, portal eligibility, per-appliance authorizations) and
 * the appliance (verification, staging, apply/rollback). Browser-safe: no Node
 * imports. Signature checks are done by each consumer with node:crypto against
 * the constants below.
 *
 * Product rule (decided 2026-09-14):
 * - Licences are perpetual; maintenance is optional and yearly.
 * - An appliance may install any version RELEASED while its coverage was
 *   active, forever. Later versions need maintenance renewed. The licence
 *   itself never stops working.
 * - Coverage for an appliance = the latest of its active licence's issued_at
 *   and the maintenance paid-through date (see `computeCoveredUntil`).
 * - Enforcement works air-gapped: the portal only offers eligible releases,
 *   AND every download carries an authorization signed by the cloud for that
 *   hardware ID, which the appliance verifies offline before installing. A
 *   package copied to another appliance without its own authorization does
 *   not install.
 */

// ── Keys ───────────────────────────────────────────────────────────────────

/**
 * Ed25519 public key (SPKI DER, base64) that PACKAGE MANIFESTS verify against.
 *
 * A separate key pair from the licence key. Its private half never touches the
 * cloud: it stays offline with staff (scripts/.update-key.pem, gitignored) and
 * is used only by scripts/build-update.ts. A stolen cloud therefore cannot ship
 * code to appliances — it can only authorize releases staff already signed.
 *
 * Generated 2026-09-15 with `npx tsx scripts/build-update.ts --generate-keys`.
 * Rotating it strands appliances on this build: see generateKeys() in that
 * script before replacing it.
 */
export const UPDATE_PUBLIC_KEY_SPKI_DER_B64 = 'MCowBQYDK2VwAyEA1YCW9VuGVluergWPH9B9jkck7Tk0ev33fxUe6NsNTSE=';

/**
 * Authorizations are signed with the LICENCE signing key
 * (LICENSE_PUBLIC_KEY_SPKI_DER_B64 in licensing.ts; the cloud's
 * LICENSE_SIGNING_KEY). The cloud already holds that key to issue licences,
 * and an authorization is the same kind of statement — "this hardware ID is
 * entitled to X" — so no new online secret is needed and the offline update key
 * stays offline.
 *
 * Domain separation: the signature covers `UPDATE_AUTHORIZATION_SIGNING_PREFIX`
 * followed by the authorization JSON bytes, never the JSON alone, so a
 * signature made for an authorization can never be replayed as a licence-key
 * signature (which covers bare payload JSON) or vice versa.
 */
export const UPDATE_AUTHORIZATION_SIGNING_PREFIX = 'scada-ink/update-authorization/v1\n';

// ── Package file format (.siupdate) ────────────────────────────────────────
//
// All integers big-endian. One file:
//
//   offset  size  field
//   0       8     magic "SIUPDATE"
//   8       1     format version = 1
//   9       1     reserved = 0
//   10      2     signature length S (uint16) = 64
//   12      4     manifest length M (uint32), 1..UPDATE_MAX_MANIFEST_BYTES
//   16      M     manifest: UTF-8 JSON, UpdateManifest
//   16+M    S     Ed25519 signature over exactly the M manifest bytes,
//                 by the update key (UPDATE_PUBLIC_KEY_SPKI_DER_B64)
//   16+M+S  P     payload: gzip-compressed ustar archive, P = manifest.payloadBytes,
//                 sha256(payload) = manifest.payloadSha256
//
// Optionally followed by ONE authorization section (added by the cloud on
// download; staff packages and the stored release never carry one):
//
//   0       8     magic "SIUAUTH1"
//   8       2     signature length S2 (uint16) = 64
//   10      2     reserved = 0
//   12      4     authorization length A (uint32), 1..UPDATE_MAX_AUTHORIZATION_BYTES
//   16      A     authorization: UTF-8 JSON, UpdateAuthorization
//   16+A    S2    Ed25519 signature over PREFIX + the A bytes, by the licence key
//
// The file must end exactly there: trailing bytes are rejected. Because the
// payload length is inside the signed manifest, the authorization cannot be
// confused with payload bytes, and appending it never changes the signed part.
//
// Payload archive rules (enforced on extract): ustar regular files and
// directories only; no absolute paths, no `..`, no symlinks/hardlinks/devices/
// pax/GNU long-name entries; every entry lies under one of
// UPDATE_MANAGED_PATHS. Files get mode 0644 (0755 when any execute bit was
// set), directories 0755.

export const UPDATE_PACKAGE_MAGIC = 'SIUPDATE';
export const UPDATE_PACKAGE_FORMAT_VERSION = 1;
export const UPDATE_AUTHORIZATION_MAGIC = 'SIUAUTH1';
export const UPDATE_HEADER_BYTES = 16;
export const UPDATE_SIGNATURE_BYTES = 64;
export const UPDATE_MAX_MANIFEST_BYTES = 64 * 1024;
export const UPDATE_MAX_AUTHORIZATION_BYTES = 4 * 1024;
export const UPDATE_FILE_EXTENSION = '.siupdate';
export const UPDATE_PRODUCT = 'scada-ink-appliance';

/**
 * What an update replaces, relative to the install root (/opt/scadaink). After
 * the one-time layout change each of these is a symlink into
 * `releases/current/<path>`, so switching `current` switches all of them in one
 * atomic rename. Nothing else in the install root is ever touched: not .env,
 * node_modules, apps/api/data (JWT secrets, uploads), apps/drivers/.venv, nor
 * anything under /var/lib/scadaink.
 */
export const UPDATE_MANAGED_PATHS = [
  'apps/api/dist',
  'apps/web/dist',
  'apps/drivers/src',
  'apps/drivers/lib',
  'apps/drivers/requirements.txt',
] as const;
export type UpdateManagedPath = (typeof UPDATE_MANAGED_PATHS)[number];

/** Must be present in every payload. `apps/drivers/lib` is optional. */
export const UPDATE_REQUIRED_PATHS: UpdateManagedPath[] = [
  'apps/api/dist',
  'apps/web/dist',
  'apps/drivers/src',
  'apps/drivers/requirements.txt',
];

/** Signed by the update key. Field order is irrelevant; the bytes are what is signed. */
export interface UpdateManifest {
  v: 1;
  product: typeof UPDATE_PRODUCT;
  /** Semver, e.g. 1.4.0 or 1.4.0-rc.1. */
  version: string;
  /** ISO 8601 UTC. The date coverage is compared against. */
  releasedAt: string;
  /** The installed version must be >= this to apply the update. */
  minFromVersion?: string;
  /** Release notes, plain text (may contain newlines). */
  notes: string;
  /**
   * Things an operator must know BEFORE installing — above all, anything the
   * new version deletes or changes on its first start. Shown prominently on
   * the staged update, and acknowledged before Install is allowed. Optional:
   * an appliance built before this field existed ignores it, which is why the
   * builder also writes the same text at the top of `notes`.
   */
  upgradeNotices?: UpgradeNotice[];
  /** Lowercase hex sha256 of the payload section. */
  payloadSha256: string;
  payloadBytes: number;
  /** Top-level paths present in the payload (a subset of UPDATE_MANAGED_PATHS). */
  contents: UpdateManagedPath[];
  /**
   * sha256 (hex) of canonical JSON of apps/api/package.json's `dependencies`
   * and `optionalDependencies` (see `canonicalDependencyJson`). The appliance
   * cannot run npm offline, so a release whose Node dependencies differ from
   * the installed ones is refused and must be installed with install-pi.sh.
   */
  nodeDependenciesSha256: string;
  /** sha256 (hex) of apps/drivers/requirements.txt. pip runs only when it changes. */
  requirementsSha256: string;
  /** Free-form build provenance (git commit, builder). Informational. */
  build?: { commit?: string; builtBy?: string };
}

/**
 * One pre-install notice. `since` is the release that introduced the change:
 * an appliance already on that version or later has been through it, so the
 * notice is not shown to it again.
 */
export interface UpgradeNotice {
  since: string;
  title: string;
  /** Plain text, may contain newlines. */
  body: string;
}

export const UPGRADE_NOTICE_LIMITS = { count: 20, title: 200, body: 4000 } as const;

/** The notices that apply to an appliance currently on `installedVersion`. */
export function upgradeNoticesFor(manifest: Pick<UpdateManifest, 'upgradeNotices'>, installedVersion: string | null): UpgradeNotice[] {
  const all = manifest.upgradeNotices ?? [];
  if (!installedVersion || !parseSemver(installedVersion)) return all;
  return all.filter((n) => compareSemver(installedVersion, n.since) < 0);
}

/** Signed by the cloud with the licence key, for ONE hardware ID and ONE release. */
export interface UpdateAuthorization {
  v: 1;
  typ: 'scada-ink-update-authorization';
  /** Normalised hardware ID (normalizeHardwareId). */
  hwid: string;
  releaseVersion: string;
  /** Must equal the package manifest's payloadSha256. */
  releaseSha256: string;
  /** ISO. The package installs only if manifest.releasedAt <= coveredUntil. */
  coveredUntil: string;
  /** ISO, when the cloud signed it. Informational (appliance clocks are not trusted). */
  issuedAt: string;
}

export interface ParsedPackageHeader {
  formatVersion: number;
  manifestOffset: number;
  manifestLength: number;
  signatureOffset: number;
  signatureLength: number;
  /** Offset of the payload section. Its length comes from the manifest. */
  payloadOffset: number;
}

const enc = new TextEncoder();
const dec = new TextDecoder('utf-8', { fatal: true });

function asciiAt(buf: Uint8Array, offset: number, len: number): string {
  let s = '';
  for (let i = 0; i < len; i++) s += String.fromCharCode(buf[offset + i]);
  return s;
}

/** Parse the fixed 16-byte package header. Returns an error string instead of throwing. */
export function parsePackageHeader(buf: Uint8Array): ParsedPackageHeader | string {
  if (buf.length < UPDATE_HEADER_BYTES) return 'The file is too short to be a Scada.Ink update package.';
  if (asciiAt(buf, 0, 8) !== UPDATE_PACKAGE_MAGIC) return 'This is not a Scada.Ink update package (.siupdate).';
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const formatVersion = buf[8];
  if (formatVersion !== UPDATE_PACKAGE_FORMAT_VERSION) {
    return `This update package uses format ${formatVersion}, which this appliance does not understand.`;
  }
  const signatureLength = view.getUint16(10);
  const manifestLength = view.getUint32(12);
  if (signatureLength !== UPDATE_SIGNATURE_BYTES) return 'The update package header is malformed (signature length).';
  if (manifestLength < 2 || manifestLength > UPDATE_MAX_MANIFEST_BYTES) return 'The update package header is malformed (manifest length).';
  return {
    formatVersion,
    manifestOffset: UPDATE_HEADER_BYTES,
    manifestLength,
    signatureOffset: UPDATE_HEADER_BYTES + manifestLength,
    signatureLength,
    payloadOffset: UPDATE_HEADER_BYTES + manifestLength + signatureLength,
  };
}

export function encodePackageHeader(manifestLength: number): Uint8Array {
  const out = new Uint8Array(UPDATE_HEADER_BYTES);
  out.set(enc.encode(UPDATE_PACKAGE_MAGIC), 0);
  out[8] = UPDATE_PACKAGE_FORMAT_VERSION;
  const view = new DataView(out.buffer);
  view.setUint16(10, UPDATE_SIGNATURE_BYTES);
  view.setUint32(12, manifestLength);
  return out;
}

/** The 16-byte header of an authorization section. */
export function encodeAuthorizationHeader(authorizationLength: number): Uint8Array {
  const out = new Uint8Array(UPDATE_HEADER_BYTES);
  out.set(enc.encode(UPDATE_AUTHORIZATION_MAGIC), 0);
  const view = new DataView(out.buffer);
  view.setUint16(8, UPDATE_SIGNATURE_BYTES);
  view.setUint32(12, authorizationLength);
  return out;
}

/**
 * Parse an authorization section (everything after the payload). Does NOT
 * check the signature. `section` must be exactly the trailer.
 */
export function parseAuthorizationSection(
  section: Uint8Array,
): { authorizationBytes: Uint8Array; signature: Uint8Array; authorization: UpdateAuthorization } | string {
  if (section.length < UPDATE_HEADER_BYTES || asciiAt(section, 0, 8) !== UPDATE_AUTHORIZATION_MAGIC) {
    return 'The update package has unexpected data after the payload.';
  }
  const view = new DataView(section.buffer, section.byteOffset, section.byteLength);
  const sigLen = view.getUint16(8);
  const len = view.getUint32(12);
  if (sigLen !== UPDATE_SIGNATURE_BYTES || len < 2 || len > UPDATE_MAX_AUTHORIZATION_BYTES) {
    return 'The update authorization is malformed.';
  }
  if (section.length !== UPDATE_HEADER_BYTES + len + sigLen) return 'The update authorization is malformed (length).';
  const authorizationBytes = section.subarray(UPDATE_HEADER_BYTES, UPDATE_HEADER_BYTES + len);
  const signature = section.subarray(UPDATE_HEADER_BYTES + len);
  let authorization: UpdateAuthorization;
  try {
    authorization = JSON.parse(dec.decode(authorizationBytes)) as UpdateAuthorization;
  } catch {
    return 'The update authorization is not valid JSON.';
  }
  const problem = validateAuthorizationShape(authorization);
  if (problem) return problem;
  return { authorizationBytes, signature, authorization };
}

/** The exact bytes the cloud signs for an authorization. */
export function authorizationSigningInput(authorizationBytes: Uint8Array): Uint8Array {
  const prefix = enc.encode(UPDATE_AUTHORIZATION_SIGNING_PREFIX);
  const out = new Uint8Array(prefix.length + authorizationBytes.length);
  out.set(prefix, 0);
  out.set(authorizationBytes, prefix.length);
  return out;
}

const SHA256_HEX = /^[0-9a-f]{64}$/;
const HWID = /^([0-9a-f]{16}|[0-9a-f]{32})$/;
const isIso = (s: unknown): s is string => typeof s === 'string' && !Number.isNaN(Date.parse(s)) && /^\d{4}-\d{2}-\d{2}T/.test(s);

/** Structural checks on a parsed manifest. Returns a message, or null when valid. */
export function validateManifestShape(m: unknown): string | null {
  if (!m || typeof m !== 'object' || Array.isArray(m)) return 'The update manifest is not an object.';
  const x = m as Partial<UpdateManifest>;
  if (x.v !== 1) return 'The update manifest version is not supported.';
  if (x.product !== UPDATE_PRODUCT) return `This package is for "${String(x.product)}", not a Scada.Ink appliance.`;
  if (typeof x.version !== 'string' || !parseSemver(x.version)) return 'The update manifest has an invalid version.';
  if (!isIso(x.releasedAt)) return 'The update manifest has an invalid release date.';
  if (x.minFromVersion !== undefined && (typeof x.minFromVersion !== 'string' || !parseSemver(x.minFromVersion))) {
    return 'The update manifest has an invalid minimum version.';
  }
  if (typeof x.notes !== 'string' || x.notes.length > 20_000) return 'The update manifest has invalid release notes.';
  if (x.upgradeNotices !== undefined) {
    const n = x.upgradeNotices;
    const L = UPGRADE_NOTICE_LIMITS;
    if (!Array.isArray(n) || n.length > L.count || n.some((e) => !e || typeof e !== 'object'
      || typeof e.since !== 'string' || !parseSemver(e.since)
      || typeof e.title !== 'string' || !e.title.trim() || e.title.length > L.title
      || typeof e.body !== 'string' || !e.body.trim() || e.body.length > L.body)) {
      return 'The update manifest has invalid upgrade notices.';
    }
  }
  if (typeof x.payloadSha256 !== 'string' || !SHA256_HEX.test(x.payloadSha256)) return 'The update manifest has an invalid payload hash.';
  if (typeof x.payloadBytes !== 'number' || !Number.isSafeInteger(x.payloadBytes) || x.payloadBytes <= 0) {
    return 'The update manifest has an invalid payload size.';
  }
  if (!Array.isArray(x.contents) || x.contents.some((c) => !(UPDATE_MANAGED_PATHS as readonly string[]).includes(c))) {
    return 'The update manifest lists contents this appliance does not manage.';
  }
  const missing = UPDATE_REQUIRED_PATHS.filter((p) => !x.contents!.includes(p));
  if (missing.length) return `The update package is missing ${missing.join(', ')}.`;
  if (typeof x.nodeDependenciesSha256 !== 'string' || !SHA256_HEX.test(x.nodeDependenciesSha256)) {
    return 'The update manifest has an invalid dependency hash.';
  }
  if (typeof x.requirementsSha256 !== 'string' || !SHA256_HEX.test(x.requirementsSha256)) {
    return 'The update manifest has an invalid requirements hash.';
  }
  return null;
}

export function validateAuthorizationShape(a: unknown): string | null {
  if (!a || typeof a !== 'object' || Array.isArray(a)) return 'The update authorization is not an object.';
  const x = a as Partial<UpdateAuthorization>;
  if (x.v !== 1 || x.typ !== 'scada-ink-update-authorization') return 'The update authorization version is not supported.';
  if (typeof x.hwid !== 'string' || !HWID.test(x.hwid)) return 'The update authorization has an invalid hardware ID.';
  if (typeof x.releaseVersion !== 'string' || !parseSemver(x.releaseVersion)) return 'The update authorization has an invalid version.';
  if (typeof x.releaseSha256 !== 'string' || !SHA256_HEX.test(x.releaseSha256)) return 'The update authorization has an invalid release hash.';
  if (!isIso(x.coveredUntil) || !isIso(x.issuedAt)) return 'The update authorization has an invalid date.';
  return null;
}

/**
 * Canonical JSON of the runtime Node dependencies: keys sorted, both maps
 * always present. Hash it with sha256 for `nodeDependenciesSha256`.
 */
export function canonicalDependencyJson(pkg: { dependencies?: Record<string, string>; optionalDependencies?: Record<string, string> }): string {
  const sort = (o: Record<string, string> | undefined) =>
    Object.fromEntries(Object.entries(o ?? {}).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
  return JSON.stringify({ dependencies: sort(pkg.dependencies), optionalDependencies: sort(pkg.optionalDependencies) });
}

// ── Versions ───────────────────────────────────────────────────────────────

export interface Semver {
  major: number;
  minor: number;
  patch: number;
  prerelease: string[];
}

const SEMVER = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?$/;

export function parseSemver(v: string): Semver | null {
  const m = SEMVER.exec(v.trim());
  if (!m || v.length > 40) return null;
  return { major: +m[1], minor: +m[2], patch: +m[3], prerelease: m[4] ? m[4].split('.') : [] };
}

/** -1, 0 or 1 by semver precedence (build metadata unsupported). Invalid versions sort first. */
export function compareSemver(a: string, b: string): number {
  const x = parseSemver(a);
  const y = parseSemver(b);
  if (!x || !y) return !x && !y ? 0 : !x ? -1 : 1;
  for (const k of ['major', 'minor', 'patch'] as const) if (x[k] !== y[k]) return x[k] < y[k] ? -1 : 1;
  if (!x.prerelease.length || !y.prerelease.length) {
    return x.prerelease.length === y.prerelease.length ? 0 : x.prerelease.length ? -1 : 1;
  }
  for (let i = 0; i < Math.max(x.prerelease.length, y.prerelease.length); i++) {
    const p = x.prerelease[i];
    const q = y.prerelease[i];
    if (p === undefined) return -1;
    if (q === undefined) return 1;
    if (p === q) continue;
    const pn = /^\d+$/.test(p);
    const qn = /^\d+$/.test(q);
    if (pn && qn) return +p < +q ? -1 : 1;
    if (pn !== qn) return pn ? -1 : 1;
    return p < q ? -1 : 1;
  }
  return 0;
}

// ── Coverage ───────────────────────────────────────────────────────────────

/** One licence row for a hardware ID, as far as coverage cares. */
export interface CoverageLicence {
  status: 'pending_payment' | 'active' | 'superseded' | 'void';
  /** Only purchases (paid or complimentary) grant coverage; imports never do. */
  source: 'purchase' | 'import';
  /** Trial licences never grant coverage. Absent = perpetual. */
  kind?: 'perpetual' | 'trial';
  issuedAt: string | null;
  maintenanceProvider: 'stripe' | 'invoice' | null;
  maintenanceActive: boolean;
  /** Current period end while active; the last period end after it lapsed. */
  maintenanceRenewsAt: string | null;
}

/**
 * The date up to which releases are covered for one appliance, or null when it
 * has no active purchased licence (no updates at all).
 *
 *   max( issued_at of each ACTIVE purchase,
 *        maintenance paid-through of each active or superseded purchase )
 *
 * Paid-through: while maintenance is active, its current period end (or `now`
 * if the provider has not reported one yet). After it lapsed or was cancelled,
 * the last period end, capped at `now` (an invoice cancellation keeps a
 * renewal date in the future; the cap stops it covering releases published
 * after the cancellation). Releases before the cap stay covered forever.
 * Superseded rows count because an upgrade moves
 * active maintenance onto the new row but a lapsed period stays on the old one.
 */
export function computeCoveredUntil(licences: CoverageLicence[], now: Date = new Date()): string | null {
  const purchases = licences.filter((l) => l.source === 'purchase' && l.kind !== 'trial');
  const active = purchases.filter((l) => l.status === 'active');
  if (!active.length) return null;
  let best = -Infinity;
  for (const l of active) if (l.issuedAt) best = Math.max(best, Date.parse(l.issuedAt));
  for (const l of purchases) {
    if (l.status !== 'active' && l.status !== 'superseded') continue;
    if (!l.maintenanceProvider) continue;
    const renews = l.maintenanceRenewsAt ? Date.parse(l.maintenanceRenewsAt) : NaN;
    if (l.maintenanceActive) best = Math.max(best, Number.isNaN(renews) ? now.getTime() : renews);
    else if (!Number.isNaN(renews)) best = Math.max(best, Math.min(renews, now.getTime()));
  }
  return Number.isFinite(best) ? new Date(best).toISOString() : null;
}

export type UpdateEligibilityReason = 'covered' | 'not_covered' | 'no_license';

export function releaseEligibility(
  releasedAt: string,
  coveredUntil: string | null,
): { eligible: boolean; reason: UpdateEligibilityReason; message: string } {
  if (!coveredUntil) {
    return {
      eligible: false,
      reason: 'no_license',
      message: 'This appliance has no active purchased license in this account, so it has no software update coverage.',
    };
  }
  if (Date.parse(releasedAt) <= Date.parse(coveredUntil)) {
    return { eligible: true, reason: 'covered', message: 'Released while this appliance was covered.' };
  }
  return {
    eligible: false,
    reason: 'not_covered',
    message: notCoveredMessage(coveredUntil),
  };
}

/** Shared wording for the portal and the appliance. */
export function notCoveredMessage(coveredUntil: string): string {
  return `This update was released after this appliance's maintenance coverage ended on ${coveredUntil.slice(0, 10)}. Renew maintenance in your Scada.Ink Cloud account to install it.`;
}

// ── Staff API: /api/admin/releases (admin role for writes) ─────────────────
//
//   POST   /api/admin/releases              body: the raw .siupdate bytes,
//                                            Content-Type: application/octet-stream
//                                            → 201 AdminRelease (unpublished)
//          The signature, manifest, payload hash and exact length are verified
//          server-side; a package that already carries an authorization, or a
//          version that already exists, is refused (409).
//   GET    /api/admin/releases              → AdminRelease[] (newest version first)
//   PATCH  /api/admin/releases/:id          UpdateReleaseRequest → AdminRelease
//   DELETE /api/admin/releases/:id          unpublished releases only (409 otherwise)
//   GET    /api/admin/releases/downloads    ?releaseId=&accountId=&limit= (≤500, default 100)
//                                            → AdminUpdateDownload[]
//
// GET routes need any staff session; POST/PATCH/DELETE need role admin.

export interface AdminRelease {
  id: string;
  version: string;
  releasedAt: string;
  minFromVersion: string | null;
  notes: string;
  payloadSha256: string;
  /** Size of the stored .siupdate file. */
  bytes: number;
  published: boolean;
  publishedAt: string | null;
  uploadedBy: string;
  createdAt: string;
  updatedAt: string;
  downloads: number;
  /** The full signed manifest. */
  manifest: UpdateManifest;
}

export interface UpdateReleaseRequest {
  published?: boolean;
  /** Portal display notes. The signed manifest's notes are unchanged. Max 20 000 chars. */
  notes?: string;
}

export interface AdminUpdateDownload {
  id: string;
  at: string;
  accountId: string;
  company: string;
  userId: string;
  userEmail: string;
  hardwareId: string;
  releaseId: string;
  version: string;
  coveredUntil: string;
}

// ── Portal API: /api/portal/updates (owners and members; viewers get 403) ──
//
//   GET /api/portal/updates                     → PortalUpdatesResponse
//   GET /api/portal/updates/:releaseId/download?hardwareId=<hwid>
//       → 200 application/octet-stream, Content-Disposition:
//         attachment; filename="scada-ink-<version>-<last 4 of hwid>.siupdate"
//         The stored package with a freshly signed authorization section appended.
//       → 403 when the release is not eligible for that appliance (JSON error
//         with the reason's message), 404 unknown/unpublished release or an
//         appliance that is not this account's, 503 when authorizations cannot
//         be signed. Every successful download is audited.
//   A plain link works: the SameSite=Lax session cookie is sent on top-level
//   navigation from the portal site.

export interface PortalRelease {
  id: string;
  version: string;
  releasedAt: string;
  minFromVersion: string | null;
  notes: string;
  bytes: number;
}

export interface PortalApplianceRelease extends PortalRelease {
  eligible: boolean;
  reason: UpdateEligibilityReason;
  message: string;
  /** Newer than the version the appliance last reported; null when unknown. */
  newerThanInstalled: boolean | null;
}

export interface PortalUpdateAppliance {
  hardwareId: string;
  /** Registered appliance id and name, when registered to this account. */
  applianceId: string | null;
  applianceName: string | null;
  /** The active purchased licence on this hardware ID, if any. */
  licenseId: string | null;
  coveredUntil: string | null;
  maintenanceActive: boolean;
  maintenanceRenewsAt: string | null;
  /** From the latest heartbeat, when the appliance reports health. */
  installedVersion: string | null;
  installedVersionReportedAt: string | null;
  /** Every published release, newest first, with this appliance's eligibility. */
  releases: PortalApplianceRelease[];
}

export interface PortalUpdatesResponse {
  appliances: PortalUpdateAppliance[];
  /** Newest published release, or null. */
  latestVersion: string | null;
}

// ── Appliance API: /api/admin/software-update (admin role) ─────────────────
//
//   GET  /status                          → SoftwareUpdateStatus
//   POST /upload[?reinstall=1]            body: raw .siupdate (application/octet-stream)
//        → 200 SoftwareUpdateVerification (verified and staged)
//        → 422 { success:false, error, data: SoftwareUpdateVerification } (rejected)
//   POST /apply                           { version } → 202 SoftwareUpdateStatus (job started)
//   POST /rollback                        → 202 SoftwareUpdateStatus (job started)
//   DELETE /staged                        → SoftwareUpdateStatus (discard the staged release)

export type SoftwareUpdateRejection =
  | 'disabled'
  | 'too_large'
  | 'malformed'
  | 'package_signature'
  | 'payload_hash'
  | 'no_authorization'
  | 'authorization_signature'
  | 'authorization_mismatch'
  | 'no_hardware_id'
  | 'wrong_hardware'
  | 'not_covered'
  | 'older_version'
  | 'same_version'
  | 'min_from_version'
  | 'dependencies_changed'
  | 'contents'
  | 'disk_space'
  | 'busy';

export interface SoftwareUpdateVerification {
  ok: boolean;
  rejection: SoftwareUpdateRejection | null;
  message: string;
  /** Present once the manifest parsed, even when the package was rejected later. */
  manifest: UpdateManifest | null;
  /** Present once the authorization parsed. Trusted only when ok. */
  authorization: UpdateAuthorization | null;
  installedVersion: string;
  /** Python requirements differ from the installed release (pip will run during apply). */
  requirementsChanged: boolean | null;
}

export type SoftwareUpdatePhase =
  | 'queued'
  | 'preparing'
  | 'installing_requirements'
  | 'switching'
  | 'restarting'
  | 'health_check'
  | 'rolling_back'
  | 'done';

export type SoftwareUpdateOutcome = 'success' | 'rolled_back' | 'failed' | 'rollback_failed';

export interface SoftwareUpdateHistoryEntry {
  action: 'apply' | 'rollback';
  fromVersion: string | null;
  toVersion: string;
  outcome: SoftwareUpdateOutcome;
  error: string | null;
  startedAt: string;
  finishedAt: string;
}

export interface SoftwareUpdateJob {
  action: 'apply' | 'rollback';
  targetVersion: string;
  phase: SoftwareUpdatePhase;
  startedAt: string;
  updatedAt: string;
  message: string;
}

export interface SoftwareUpdatePrerequisite {
  id: 'root' | 'api_unit' | 'updater_unit' | 'updater_script' | 'sudo' | 'disk';
  ok: boolean;
  message: string;
}

export interface SoftwareUpdateStatus {
  enabled: boolean;
  hardwareId: string | null;
  installedVersion: string;
  installedReleasedAt: string | null;
  /** 'flat': a manual install (no release directories yet). 'releases': symlinked layout. */
  layout: 'flat' | 'releases';
  previousVersion: string | null;
  staged: { manifest: UpdateManifest; authorization: UpdateAuthorization; stagedAt: string; reinstall: boolean } | null;
  job: SoftwareUpdateJob | null;
  lastResult: SoftwareUpdateHistoryEntry | null;
  history: SoftwareUpdateHistoryEntry[];
  prerequisites: SoftwareUpdatePrerequisite[];
  /** All prerequisites pass: apply/rollback can start. */
  ready: boolean;
}
